import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronUp, Plus, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading, EmptyState } from "@/components/shared";
import api from "@/lib/api";

const STATUS_COLORS = {
  "Submitted": "bg-slate-100 text-slate-600", "Under Review": "bg-amber-100 text-amber-700",
  "Planned": "bg-blue-100 text-blue-700", "In Production": "bg-purple-100 text-purple-700",
  "Published": "bg-emerald-100 text-emerald-700", "Declined": "bg-red-100 text-red-600",
};

export default function Requests() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Playbook", business_problem: "" });
  const { data, isLoading, refetch } = useQuery({ queryKey: ["requests"], queryFn: async () => (await api.get("/requests")).data });

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/requests", form); toast.success("Request submitted!"); setOpen(false); setForm({ title: "", description: "", category: "Playbook", business_problem: "" }); refetch(); }
    catch { toast.error("Could not submit"); }
  };
  const vote = async (id) => { try { await api.post(`/requests/${id}/vote`); refetch(); } catch { toast.error("Could not vote"); } };

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center justify-between">
        <div><h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Request & Vote</h1><p className="mt-1 text-slate-600">Request new resources and vote on what we build next.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="new-request-btn"><Plus className="mr-1.5 h-4 w-4" /> New request</Button></DialogTrigger>
          <DialogContent data-testid="new-request-dialog">
            <DialogHeader><DialogTitle>Request a resource</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input placeholder="What should we build?" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="request-title" />
              <Textarea placeholder="Describe it and why it helps…" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="request-desc" />
              <Input placeholder="Business problem it solves" value={form.business_problem} onChange={(e) => setForm({ ...form, business_problem: e.target.value })} data-testid="request-problem" />
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="request-submit">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {isLoading ? <Loading /> : (data?.length ? (
        <div className="space-y-3">
          {data.map((r) => (
            <div key={r.id} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5" data-testid={`request-${r.id}`}>
              <button onClick={() => vote(r.id)} data-testid={`request-vote-${r.id}`}
                className={`flex flex-col items-center rounded-lg border px-3 py-2 transition-colors ${r.voted ? "border-emerald bg-emerald/10 text-emerald" : "border-slate-200 text-slate-500 hover:border-emerald"}`}>
                <ChevronUp className="h-5 w-5" /><span className="text-sm font-bold">{r.vote_count}</span>
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-heading font-semibold text-navy">{r.title}</h3>
                  <span className={`rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${STATUS_COLORS[r.status]}`}>{r.status}</span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{r.description}</p>
                {r.admin_response && <p className="mt-2 rounded-lg bg-slate-50 p-2 text-sm text-slate-600"><span className="font-semibold text-navy">Team: </span>{r.admin_response}</p>}
                <p className="mt-2 flex items-center gap-1 text-xs text-slate-400"><MessageSquare className="h-3.5 w-3.5" /> {r.comments?.length || 0} comments · by {r.created_by_name}</p>
              </div>
            </div>
          ))}
        </div>
      ) : <EmptyState title="No requests yet" subtitle="Be the first to request a resource." />)}
    </DashboardLayout>
  );
}
